import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CronogramaFase, ObraConfig } from '@services/db.js'
import { getObraConfig } from '@services/crud.js'
import {
  getCronograma,
  resumirCronograma,
  saveCronogramaDatas,
  sincronizarFaseConstrutiva,
  sugerirDatasCronograma,
  upsertCronogramaFase,
  type CronogramaResumo,
} from '@services/gestao.js'
import { FASES_CONSTRUTIVAS } from '@utils/constants.js'

export type FaseStatus = 'nao-iniciada' | 'em-andamento' | 'concluida' | 'atrasada'

export interface FaseItem extends CronogramaFase {
  label: string
  desc: string
  ordem: number
  status: FaseStatus
  /** Dias de atraso em relação ao fim previsto (0 se em dia). */
  atrasoDias: number
  /** Duração prevista em dias, quando ambas as datas existem. */
  duracaoDias: number | null
}

const today = () => new Date().toISOString().slice(0, 10)

function diffDays(a: string, b: string): number {
  const ms = new Date(`${b}T12:00:00`).getTime() - new Date(`${a}T12:00:00`).getTime()
  return Math.round(ms / 86_400_000)
}

function statusDaFase(f: CronogramaFase, hoje: string): FaseStatus {
  if (f.avancoPct >= 100) return 'concluida'
  if (f.fimPrevisto && f.fimPrevisto < hoje) return 'atrasada'
  if (f.avancoPct > 0) return 'em-andamento'
  return 'nao-iniciada'
}

export function useCronograma() {
  const [fases, setFases] = useState<CronogramaFase[]>([])
  const [config, setConfig] = useState<ObraConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    const [rows, cfg] = await Promise.all([getCronograma(), getObraConfig()])
    setFases(rows)
    setConfig(cfg ?? null)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const itens = useMemo<FaseItem[]>(() => {
    const hoje = today()
    return fases.map((f, i) => {
      const meta = FASES_CONSTRUTIVAS[i]!
      const status = statusDaFase(f, hoje)
      return {
        ...f,
        label: meta.label,
        desc: meta.desc,
        ordem: i + 1,
        status,
        atrasoDias: status === 'atrasada' && f.fimPrevisto ? diffDays(f.fimPrevisto, hoje) : 0,
        duracaoDias:
          f.inicioPrevisto && f.fimPrevisto ? diffDays(f.inicioPrevisto, f.fimPrevisto) + 1 : null,
      }
    })
  }, [fases])

  const resumo = useMemo<CronogramaResumo>(
    () => resumirCronograma(fases, config?.dataInicio, config?.dataPrevisaoTermino),
    [fases, config],
  )

  const temDatas = useMemo(() => fases.some((f) => f.inicioPrevisto || f.fimPrevisto), [fases])

  const definirAvanco = useCallback(
    async (faseId: string, avancoPct: number) => {
      await upsertCronogramaFase(faseId, { avancoPct })
      const rows = await getCronograma()
      const r = resumirCronograma(rows, config?.dataInicio, config?.dataPrevisaoTermino)
      await sincronizarFaseConstrutiva(r.faseAtualId)
      await load()
    },
    [load, config],
  )

  const editarFase = useCallback(
    async (
      faseId: string,
      patch: Partial<Omit<CronogramaFase, 'id' | 'faseId' | 'atualizadoEm' | 'avancoPct'>>,
    ) => {
      await upsertCronogramaFase(faseId, patch)
      await load()
    },
    [load],
  )

  const aplicarSugestao = useCallback(
    async (dataInicio: string, duracaoDias: number) => {
      await saveCronogramaDatas(sugerirDatasCronograma(dataInicio, duracaoDias))
      await load()
    },
    [load],
  )

  return {
    itens,
    resumo,
    config,
    temDatas,
    isLoading,
    definirAvanco,
    editarFase,
    aplicarSugestao,
    reload: load,
  }
}
